import type { MessageDescriptor } from 'react-intl';
import { z } from 'zod';

// eslint-disable-next-line no-restricted-imports
import type * as Logo from '@atlaskit/logo';

import { ChargeType, EditionType, PricingType, UiProductType } from '@adminhub/graphql-types';
import type * as Image from '@adminhub/images';

import { checkMapKeyMatches, checkObjectKeysAreSorted } from './common';

type AkLogoPackageName = keyof Omit<typeof Logo, 'LogoProps'>;
type AKIconPackageName = AkLogoPackageName;

type IconPackageName = keyof typeof Image;

const ariResourceOwner = z
  .string()
  .describe(
    'The resourceOwner portion of the ARI (i.e. ari:cloud:<resourceOwner>::site/<id>). See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/.',
  );

const MessageSchema = z.custom<MessageDescriptor>();

/**
 * The base schema for the product config. These are fields that are required for all products.
 */
const BaseJsonSchemaFields = {
  ariResourceOwner,
  productName: z.string().nonempty().describe('The name of the product.'),
};

const BaseJsonSchema = z.object(BaseJsonSchemaFields).strict();

/**
 * The schema for the additional input options. These are fields that are optional and if not provided will be defaulted to the ariResourceOwner.
 */
const AdditionalInputOptionsJsonSchemaFields = {
  type: z.nativeEnum(UiProductType).describe('The UI product type.'),
  idOrgManagerProductKey: z
    .string()
    .describe(
      'The `productKey` used by `id-org-manager` service. See https://bitbucket.org/atlassian/id-org-manager/src/master/src/main/java/com/atlassian/orgmanager/services/products/ProductsData.java. Defaults to the ariResourceOwner.',
    ),
  userManagementProductId: z
    .string()
    .describe(
      'The `productId` that is used by `user-management` service. See https://bitbucket.org/atlassian/user-management/src/master/hipster-user-management/src/main/java/com/atlassian/uam/product/model/*. Defaults to the ariResourceOwner.',
    ),
  userManagementCanonicalProductKey: z
    .string()
    .describe(
      'The `canonicalProductKey` that is used by `user-management` service. See ResourceInfo.resourceOwner at https://bitbucket.org/atlassian/user-management/src/master/hipster-user-management/src/main/java/com/atlassian/uam/product/model/*. Defaults to the ariResourceOwner.',
    ),
  productId: z
    .string()
    .describe(
      'The productId for the product. This is used to map the product to its details and offerings in CCP. Each product must be available at https://workbench.staging.atl-paas.net/products/<productId>',
    ),
  billingProductKey: z.string().describe('The `billing offering productKey` that is used by `ccp-offerings`.'),
  connectorType: z
    .string()
    .describe(
      'The `connectorType` for third party products that is used by the `ai-3p-connector` service. This should be used sparingly until ARIs with are supported with the service. See https://bitbucket.org/atlassian/ai-3p-connector/src/main/',
    ),
};
const AdditionalInputOptionsJsonSchema = z.object(AdditionalInputOptionsJsonSchemaFields).partial();

// Create a mapped type that transforms each property value to a Zod array of the original type
type ConvertValuesToZodArrays<T extends Record<string, z.ZodTypeAny>> = {
  [K in keyof T]: z.ZodArray<T[K], 'atleastone'>;
};

/**
 * The schema for the alternative input options.
 */
const AlternativeInputOptionsJsonSchemaFields = [
  ...Object.entries(BaseJsonSchemaFields),
  ...Object.entries(AdditionalInputOptionsJsonSchemaFields),
].reduce(
  (acc, [key, value]) => {
    acc[key] = z
      .array(value)
      .nonempty()
      .describe(
        `Alternative values for ${key} of the product. This will only be accepted as possible input in fromProductConfig, but will not be returned by ProductConfig. Useful for mapping mulitple input values to a single product config (i.e. during renames).`,
      );

    return acc;
  },
  {} as ConvertValuesToZodArrays<typeof BaseJsonSchemaFields & typeof AdditionalInputOptionsJsonSchemaFields>,
);
const AlternativeInputOptionsJsonSchema = z.object(AlternativeInputOptionsJsonSchemaFields).partial();

const LinkSchema = z
  .function()
  .args(
    z.object({
      orgId: z.string().optional(),
      siteId: z.string().optional(),
      workspaceLink: z.string().optional().nullable(),
      productUrl: z.string().optional(),
      homeUrl: z.string().optional(),
      users: z.array(z.string()).optional(),
      groups: z.array(z.string()).optional(),
      usageKey: z.string().optional(),
      ccpEntitlementId: z.string().optional(),
      studioWorkspaceId: z.string().optional(),
    }),
  )
  .returns(z.string());
export type LinksSchema = z.infer<typeof Links>;
export const Links = z
  .object({
    productLink: LinkSchema.describe('Link to product. Typically shown in product actions as "Open {productName}".'),
    settingsLink: LinkSchema.describe('Link to product settings. Typically shown in product actions as "{productName} Settings".'),
    manageAppsLink: LinkSchema.describe('Link to Manage Apps.'),
    manageGuestAccessLink: LinkSchema.describe('Link to Guest Access.'),
    manageAISettingsLink: LinkSchema.describe('Link to the Rovo Dev AI settings tab within the AI Enabled Apps page.'),
    manageCreditsLink: LinkSchema.describe('Link to the platform usage credits page.'),
  })
  .partial();

export type AddProduct = z.infer<typeof AddProductSchema>;

export enum CollectionName {
  TeamworkCollection = 'Teamwork Collection',
  ServiceCollection = 'Service Collection',
}

const ProductCardSchema = z.object({
  appCardDescription: MessageSchema.describe('The description of the product to be used in the app card.').optional(),
  learnMoreUrl: z.string().describe('The URL to learn more about the product.'),
  summaryTitle: MessageSchema.describe('The title for the summary section.').optional(),
});

const AddProductSchema = z.object({
  enabled: z.boolean().describe('Whether the product should be included in the add product flow.'),
  productKey: z.string().describe('The product key.'),
  vortexOnly: z.boolean().describe('Whether to include the product in Vortex orgs only.').optional(),
  nonVortexOnly: z.boolean().describe('Whether to include the product in non-Vortex orgs only.').optional(),
  enterpriseProductKey: z.string().describe('The keys from entitlement SKU IDs').optional(),
  parentCollectionName: z.nativeEnum(CollectionName).describe('The name of the parent collection for the app').optional(),
  productCard: ProductCardSchema.describe('The card information for the product.').optional(),
  includeWithAccess: z
    .boolean()
    .describe('Whether to include a product with access in the add product flow. Defaults to false.')
    .optional(),
  includeWithExistingSites: z.boolean().describe('Whether to include the product with existing sites. Defaults to false.').optional(),
  includeWithVortex: z.boolean().describe('Whether to include the product in Vortex orgs only. Defaults to false.').optional(),
  includeWithJWMForMigrationFlow: z
    .boolean()
    .describe('Whether to include the product in JWM for migration flow. Defaults to false.')
    .optional(),
  includeWithRovoPaidProvisioning: z
    .boolean()
    .describe('Whether to include the Rovo product in the add product flow. Defaults to false.')
    .optional(),
  includeWithJSM: z.boolean().describe('Whether to include JSM in the add product flow. Defaults to false.').optional(),
  includeWithTWCEnterprise: z
    .boolean()
    .describe('Whether to include the product in the add product flow when org has TWC Enterprise entitlement. Defaults to false.')
    .optional(),
  welcomePath: z
    .string()
    .describe('The path to the welcome page of an app that was activated. Should be prefixed with site the app was provisioned on')
    .optional(),
});

/**
 * Environment where the offering is valid. Workbench has different offering IDs per environment.
 */
export const OfferingEnvironment = z.enum(['commercial', 'fedramp', 'ic']);
export type OfferingEnvironment = z.infer<typeof OfferingEnvironment>;

const Offerings = z.object({
  productKey: z.string(),
  chargeElement: z.nativeEnum(ChargeType),
  edition: z.nativeEnum(EditionType).optional(),
  pricingType: z.nativeEnum(PricingType).optional(),
  parentOfferingId: z.string().optional(),
  /**
   * Environment this offering is for. Omit or 'commercial' for default.
   * Use 'fedramp' for FedRAMP and 'ic' for Isolated Cloud so provisioning uses the correct Workbench offering.
   */
  environment: OfferingEnvironment.optional(),
});
const offeringId = z.string().describe('The product offering id. This is the key of each offering object.');
const OfferingsRecordSchema = z.record(offeringId, Offerings);
const OfferingsWithIds = z.record(offeringId, Offerings.extend({ offeringId: z.string() }));

export type OfferingsSchemaWithIds = z.infer<typeof OfferingsWithIds>;
export type OfferingsSchema = z.infer<typeof OfferingsRecordSchema>;

const WacUrlPathArgs = z.object({
  productKey: z.string().describe('The product key from AddProduct.'),
  siteName: z.string(),
  cloudId: z.string(),
  requestId: z.string(),
});
const WacUrlPathSchema = z.function().args(WacUrlPathArgs).returns(z.string());
export type WacUrlPathArgs = z.infer<typeof WacUrlPathArgs>;

const CcpWacUrlPathArgs = z.object({
  platform: z.string(),
  provisionRequestId: z.string(),
  ondemandUrl: z.string(),
  productKey: z.string(),
});
const CcpWacUrlPathSchema = z.function().args(CcpWacUrlPathArgs).returns(z.string());

export type CcpWacUrlPathArgs = z.infer<typeof CcpWacUrlPathArgs>;

export const DEFAULT_ARI_RESOURCE_TYPE = 'site';

const ProductConfigJsonSchema = BaseJsonSchema.merge(AdditionalInputOptionsJsonSchema)
  .merge(
    z
      .object({
        alternatives: AlternativeInputOptionsJsonSchema.describe(
          'The alternative input options for the product. For example, the IOM product key `jira-core` and `jira-work-management` should both be mapped to Jira Work Management.',
        ),
        ariResourceType: z
          .string()
          .describe(
            'The resourceType portion of the ARI (i.e. ari:cloud:<resourceOwner>::<resourceType>/<id>). See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/.',
          )
          .default(DEFAULT_ARI_RESOURCE_TYPE),
        localizedProductName: MessageSchema.describe(
          'The localized name of the product, used for display in the UI. This should be a translatable message descriptor to support internationalization.',
        ),
        sandboxProductKey: z
          .string()
          .describe(
            'The `sandboxProductKey` that is used by `sandbox-service`. See https://bitbucket.org/atlassian/sandbox-service/src/master/common/src/main/kotlin/com/atlassian/sandbox/model/SandboxableProductKey.kt. Defaults to the ariResourceOwner.',
          ),
        emauProductKey: z
          .string()
          .describe(
            'The product key used for xflow/product activation analytics. The emau product key, one of the standard product key sets used by xflow. See https://bitbucket.org/atlassian/growth-analytics-pipeline-airflow/src/master/configurations/business/dim_model/subdag_configurations/dimensional_model/task_scripts/sql_scripts/07_dim_product.hql. Defaults to the ariResourceOwner.',
          ),
        hamsProductKey: z
          .string()
          .describe(
            'The HAMS product key, one of the standard product key sets used by commerce in CCP graphql through AGG. See https://bitbucket.org/atlassian/ccp-graphql-gateway/src/master/src/main/kotlin/io/atlassian/commerce/ccp/graphql/gateway/dto/hams/HamsProductKeys.kt. Will be undefiend if not specified.',
          ),
        enterpriseProductKey: z
          .string()
          .describe('The enterprise product key used to determine if an organization has the enterprise edition of this product.'),
        productLogo: z
          .custom<AkLogoPackageName>((packageName) => typeof packageName === 'string' && packageName.endsWith('Logo'))
          .describe(
            'The logo component name to be used for this product in the UI. This should be the name of the React component that can be rendered by the `@atlaskit/logo` component. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the logo will be loaded via `@adminhub/product-logo`. This is optional and will default to the product name if not provided.',
          ),
        productIcon: z
          .custom<AKIconPackageName | IconPackageName>((packageName) => typeof packageName === 'string' && packageName.endsWith('Icon'))
          .describe(
            'The icon component or SVG icon asset name to be used for this product in the UI. This should be either the name of the React component that can be rendered by the `@atlaskit/logo` component or a 3P icon from `@adminhub/images`. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the icon will be loaded via `@adminhub/product-logo` or `@adminhub/images`.',
          ),
        dcProductIcon: z
          .custom<AKIconPackageName>((packageName) => typeof packageName === 'string' && packageName.endsWith('DataCenterIcon'))
          .describe(
            'The icon component name to be used for this product in the UI for Data Center products. This should be the name of the React component that can be rendered by the `@atlaskit/logo` component. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the logo will be loaded via `@adminhub/product-logo`. This is optional and will default to the product name if not provided.',
          ),
        links: Links.describe('The object will contain all the links for this product.'),
        // TODO: Migrate this into the links object. Need to handle hasLink from ProductConfig with default links. So skipping this for now.
        signUpLink: z.string().describe('WAC Link to sign up for the product. Defaults to https://www.atlassian.com/try'),
        productSettingName: z
          .string()
          .describe(
            "The display name of the app/product external settings link. This is used on the site menu to link the user out of AdminHub. Set to undefined to disable this feature as some apps don't need an external settings link (i.e. Access/Guard). Defaults to the product name.",
          ),
        addProduct: AddProductSchema.describe('Whether to include the product in the add product flow.'),
        description: MessageSchema.describe('The description of the product.'),
        summaryPoints: z.array(MessageSchema).describe('The summary points for the product.'),
        offerings: OfferingsRecordSchema.describe(
          'The offerings for the product. This is used to map the product to its offerings in CCP. Each offering must be available at https://workbench.staging.atl-paas.net/offerings/<offeringId>',
        ),
        wacUrlPath: WacUrlPathSchema.describe(
          'The WAC URL path for the product. This is used to redirect the user to the product after creation in the add product flow.  The base WAC URL is defined in @adminhub/config',
        ),
        ccpWacUrlPath: CcpWacUrlPathSchema.describe(
          'The CCP WAC URL path for the product. This is used to redirect the user to the product after creation in the add product flow.  The base WAC URL is defined in @adminhub/config',
        ),
        // TODO: Add more fields
      })
      .partial(),
  )
  .strict();

export type ProductConfigJson = z.infer<typeof ProductConfigJsonSchema>;

const checkProductConfigKeysMatch = (data: Record<string, ProductConfigJson>, ctx: z.RefinementCtx) =>
  checkMapKeyMatches(data, 'ariResourceOwner', ctx);

export const ProductConfigMapJsonSchema = z
  .object({
    $schema: z.string().optional(),
    products: z
      .record(ariResourceOwner, ProductConfigJsonSchema)
      .superRefine(checkObjectKeysAreSorted)
      .superRefine(checkProductConfigKeysMatch),
    productOverrides: z
      .record(
        z.string().nonempty().describe('Feature gate for overriding the product config.'),
        z.record(ariResourceOwner, ProductConfigJsonSchema).superRefine(checkObjectKeysAreSorted).superRefine(checkProductConfigKeysMatch),
      )
      .superRefine(checkObjectKeysAreSorted)
      .optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (!data.productOverrides) {
      return;
    }

    // Check if the productOverrides have existing keys in the products
    for (const productOverride of Object.values(data.productOverrides)) {
      for (const productKey of Object.keys(productOverride)) {
        if (productKey in data.products) {
          continue;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The key "${productKey}" in the productOverrides is missing in product config.`,
        });
      }
    }
  });

export type ProductConfigMapJson = z.infer<typeof ProductConfigMapJsonSchema>;
